import React, { useState, useRef, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  TextInputWithLabel,
  Drawer,
  Button,
  PopupMenu,
  Loader,
  DatePicker
} from 'quantux-ui';
import { BASE_URL, padZero } from '../../utils';
import { gray, salmon, blue } from '../../theme';
import PayerCard from '../patient-dashboard/payer-card';
import PayerCardLarge from '../payer-dashboard/payer-card';
import PayerSearchCard from './payer-search-card';

const piIcon = (
  <svg
    width="24"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm12.071-2L3.5 4.857v8.572A8.57 8.57 0 0 0 12.071 22a8.57 8.57 0 0 0 8.572-8.571V4.857L12.07 2Zm.003 18.393a6.965 6.965 0 0 0 6.964-6.964V6.063l-6.964-2.456-6.965 2.456v7.366a6.965 6.965 0 0 0 6.965 6.964Zm1.101-5.318V16h-4v-.925h1.465V10.86c0-.167.005-.338.015-.515l-1.04.87a.456.456 0 0 1-.18.09.457.457 0 0 1-.3-.05.318.318 0 0 1-.09-.085l-.39-.535L10.86 8.76h1.015v6.315h1.3Zm2.957-8.095a2.222 2.222 0 0 0-.828-.148c-.307 0-.585.05-.836.148-.25.099-.465.239-.644.42a1.906 1.906 0 0 0-.412.664 2.526 2.526 0 0 0-.144.876c0 .325.048.619.144.88.099.261.236.484.412.668.179.184.393.325.644.424.25.099.53.148.836.148.304 0 .58-.05.828-.148.25-.099.464-.24.64-.424a1.87 1.87 0 0 0 .412-.668c.096-.261.144-.555.144-.88 0-.323-.048-.615-.144-.876a1.794 1.794 0 0 0-1.052-1.084Zm-.072 2.976c-.163.23-.415.344-.756.344-.347 0-.604-.115-.772-.344-.165-.232-.248-.568-.248-1.008 0-.44.083-.776.248-1.008.168-.235.425-.352.772-.352.341 0 .593.116.756.348.165.232.248.57.248 1.012 0 .443-.083.779-.248 1.008Z"
      fill="#96C8FF"
    />
  </svg>
);

const TODAY = new Date();

const InsuranceDrawer = (props) => {
  // State variables
  const [isSaved, setIsSaved] = useState(true);
  const [payerSearch, setPayerSearch] = useState('');
  const [payerSearchResults, setPayerSearchResults] = useState([]);
  const [showRelationMenu, setShowRelationMenu] = useState(false);
  // TODO: remove if not used (2 state variables below)
  const [showFrontLoader, setShowFrontLoader] = useState(false);
  const [showBackLoader, setShowBackLoader] = useState(false);
  // --
  const [payer, setPayer] = useState({});
  const [patientRelationToMember, setPatientRelationToMember] = useState('');
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberMiddleName, setMemberMiddleName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [memberDobDate, setMemberDobDate] = useState(TODAY.getDate());
  const [memberDobMonth, setMemberDobMonth] = useState(TODAY.getMonth());
  const [memberDobYear, setMemberDobYear] = useState(TODAY.getFullYear());
  const [showMemberDobAlert, setShowMemberDobAlert] = useState(true);
  const [isMemberDobPristine, setIsMemberDobPristine] = useState(true);
  const [policyNumber, setPolicyNumber] = useState('');
  const [authorizationNumber, setAuthorizationNumber] = useState('');
  const [groupNumber, setGroupNumber] = useState('');
  const [showPayerCards, setShowPayerCards] = useState(false);

  useEffect(() => {
    setPayerSearchResults([]);
    if (payerSearch.length === 0) {
      setShowPayerCards(false);
    }
  }, [payerSearch]);

  useEffect(() => {
    setPayer(payerData);
  }, [props.payerData]);

  useEffect(() => {
    if (props.insuranceType === 'primary') {
      if (props.patientData.primaryInsurance && Object.keys(props.patientData).length > 0) {
        const {
          memberFirstName: firstName,
          memberMiddleName: middleName,
          memberLastName: lastName,
          authorizationNumber: authNumber,
          groupNumber: gpNumber,
          policyNumber: pNumber,
          memberDob,
          patientRelationToMember: pRelation2Member
        } = props.patientData.primaryInsurance;

        setPatientRelationToMember(pRelation2Member);
        setMemberFirstName(firstName);
        setMemberMiddleName(middleName);
        setMemberLastName(lastName);
        setGroupNumber(gpNumber);
        setPolicyNumber(pNumber);
        setAuthorizationNumber(authNumber);
        setShowFrontLoader(false);

        // Dob
        if (pRelation2Member && pRelation2Member.toLowerCase() === 'self') {
          const tmp1 = memberDob.split('T');
          const tmp2 = tmp1[0].split('-');
          const _year = +tmp2[0];
          const _month = +tmp2[1];
          const _date = +tmp2[2];
          setMemberDobYear(+(_year));
          setMemberDobMonth(+(_month) - 1);
          setMemberDobDate(+(_date));
          setIsMemberDobPristine(false);
          setShowMemberDobAlert(false);
        }
      }
    }

    if (props.insuranceType === 'secondary') {
      if (props.patientData.secondaryInsurance && Object.keys(props.patientData).length > 0) {
        const {
          memberFirstName: firstName,
          memberMiddleName: middleName,
          memberLastName: lastName,
          authorizationNumber: authNumber,
          groupNumber: gpNumber,
          policyNumber: pNumber,
          memberDob,
          patientRelationToMember: pRelation2Member
        } = props.patientData.secondaryInsurance;

        setPatientRelationToMember(pRelation2Member);
        setMemberFirstName(firstName);
        setMemberMiddleName(middleName);
        setMemberLastName(lastName);
        setGroupNumber(gpNumber);
        setPolicyNumber(pNumber);
        setAuthorizationNumber(authNumber);
        setShowFrontLoader(false);

        // Dob
        if (pRelation2Member && pRelation2Member.toLowerCase() === 'self') {
          const tmp1 = memberDob.split('T');
          const tmp2 = tmp1[0].split('-');
          const _year = +tmp2[0];
          const _month = +tmp2[1];
          const _date = +tmp2[2];
          setMemberDobYear(+(_year));
          setMemberDobMonth(+(_month) - 1);
          setMemberDobDate(+(_date));
          setIsMemberDobPristine(false);
          setShowMemberDobAlert(false);
        }
      }
    }

    if (props.insuranceType === 'tertiary') {
      if (props.patientData.tertiaryInsurance && Object.keys(props.patientData).length > 0) {
        const {
          memberFirstName: firstName,
          memberMiddleName: middleName,
          memberLastName: lastName,
          authorizationNumber: authNumber,
          groupNumber: gpNumber,
          policyNumber: pNumber,
          memberDob,
          patientRelationToMember: pRelation2Member
        } = props.patientData.tertiaryInsurance;

        setPatientRelationToMember(pRelation2Member);
        setMemberFirstName(firstName);
        setMemberMiddleName(middleName);
        setMemberLastName(lastName);
        setGroupNumber(gpNumber);
        setPolicyNumber(pNumber);
        setAuthorizationNumber(authNumber);
        setShowFrontLoader(false);

        // Dob
        if (pRelation2Member && pRelation2Member.toLowerCase() === 'self') {
          const tmp1 = memberDob.split('T');
          const tmp2 = tmp1[0].split('-');
          const _year = +tmp2[0];
          const _month = +tmp2[1];
          const _date = +tmp2[2];
          setMemberDobYear(+(_year));
          setMemberDobMonth(+(_month) - 1);
          setMemberDobDate(+(_date));
          setIsMemberDobPristine(false);
          setShowMemberDobAlert(false);
        }
      }
    }
  }, [props.patientData, props.insuranceType]);

  // Refs
  const payerSearchRef = useRef();
  const memberRelationRef = useRef();
  const frontCardRef = useRef();
  const backCardRef = useRef();

  // ---- Destructuring props ----
  const {
    isVisible,
    patientData,
    payerData,
    style,
    zIndex = '2010',
    placement,
    onShowWarning,
    onShowPayerDeleteWarning,
    onClose,
    onInsuranceCardFrontCameraClick,
    onInsuranceCardBackCameraClick,
    headerText,
    saveUrl = 'savePatientPrimaryInsuranceData'
  } = props;

  // -------------- Styles --------------
  const styles = StyleSheet.create({
    sideDrawerContainer: {
      borderLeft: `solid ${blue[400]}`,
      background: gray[900],
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    sideDrawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    sideDrawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerContentArea: {
      // height: 'calc(100vh - 186px)',
      // height: `${window.innerHeight - 170}px`,
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    sideDrawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    sideDrawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerUserCardText: {
      fontSize: '12px'
    },
    popupMenuContainer: {
      background: '#083050',
      width: '359px',
      height: '200px',
      maxHeight: '200px',
      overflowY: 'auto',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)'
    },
    popupMenuItem: {
      height: '60px',
      background: blue[700],
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      ':hover': {
        color: blue[200],
        background: blue[900]
      }
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60px',
      minHeight: '60px',
      width: '40px',
      minWidth: '40px'
    },
    menuItemIcon: {
      minWidth: '32px'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '6px',
      bottom: '2px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    }
  });

  // #region Event Handlers
  const handleOnChangePayerSearch = (e) => {
    const { value } = e.target;
    setPayerSearch(value);
  };

  const handleOnEditMemberFirstName = (e) => {
    const { value } = e.target;
    setMemberFirstName(value);
  };

  const handleOnEditMemberMiddleName = (e) => {
    const { value } = e.target;
    setMemberMiddleName(value);
  };

  const handleOnEditMemberLastName = (e) => {
    const { value } = e.target;
    setMemberLastName(value);
  };

  const handleOnEditPolicyNumber = (e) => {
    const { value } = e.target;
    setPolicyNumber(value);
    setIsSaved(false);
  };

  const handleOnEditAuthorizationNumber = (e) => {
    const { value } = e.target;
    setAuthorizationNumber(value);
    setIsSaved(false);
  };

  const handleOnEditGroupNumber = (e) => {
    const { value } = e.target;
    setGroupNumber(value);
    setIsSaved(false);
  };

  const handleOnSaveInsuranceData = () => {
    const dobString = `${memberDobYear}-${padZero(+memberDobMonth + 1)}-${padZero(+memberDobDate)}T00:00:00.000Z`;
    const data = {
      payer: payer._id,
      memberFirstName,
      memberMiddleName,
      memberLastName,
      memberDob: dobString,
      patientRelationToMember,
      policyNumber,
      authorizationNumber,
      groupNumber
    };

    const formData = new FormData();
    formData.append('payerId', data.payer);
    formData.append('patientId', patientData._id);
    if (data._id === '632b3f6070289c1efc119171') {
      formData.append('tmpPayerName', data.payerName.replaceAll('"', ''));
    }
    formData.append('memberFirstName', data.memberFirstName || '');
    formData.append('memberMiddleName', data.memberMiddleName || '');
    formData.append('memberLastName', data.memberLastName || '');

    if (!data.isMemberDobPristine) {
      formData.append('memberDob', data.memberDob);
    } else {
      formData.append('memberDob', 'n/a');
    }

    formData.append('patientRelationToMember', data.patientRelationToMember);
    formData.append('policyNumber', data.policyNumber || '');
    formData.append('authorizationNumber', data.authorizationNumber || '');
    formData.append('groupNumber', data.groupNumber || '');

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/${saveUrl}`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Primary Insurance data saved!');
          props.onSaveData(data);
          props.onClose();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnDeletePayer = () => {
    onShowPayerDeleteWarning();
  };
  // #endregion

  let insuranceIdFrontUrl = '';
  let insuranceIdBackUrl = '';

  if (props.insuranceType === 'primary') {
    insuranceIdFrontUrl = patientData.primaryInsuranceCardFrontScan || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
    insuranceIdBackUrl = patientData.primaryInsuranceCardBackScan || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
  } else if (props.insuranceType === 'secondary') {
    insuranceIdFrontUrl = patientData.secondaryInsuranceCardFrontScan || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
    insuranceIdBackUrl = patientData.secondaryInsuranceCardBackScan || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
  } else if (props.insuranceType === 'tertiary') {
    insuranceIdFrontUrl = patientData.tertiaryInsuranceCardFrontScan || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
    insuranceIdBackUrl = patientData.tertiaryInsuranceCardBackScan || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg';
  }

  const handleOnClickSearchPayers = () => {
    if (props.onPayerSearch) {
      props.onPayerSearch(payerSearch);
    }
    // Search existing payers
    const formData = new FormData();
    formData.append('searchTerm', payerSearch);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/payers2/searchPayersByName`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          const data = res.payers;
          setPayerSearchResults(data);
          setShowPayerCards(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  return (
    <Drawer
      placement={placement}
      disableMaskClick
      style={{ ...style }}
      visible={isVisible}
      toggleDrawer={onClose}
      zIndex={`${zIndex}`}
      showMask
      width={window.innerWidth <= 700 ? window.innerWidth : 375}
    >
      <div className={css(styles.sideDrawerContainer)}>
        {/* Header */}
        <div className={css(styles.sideDrawerHeader)}>
          <div className='fx fx__hv-center'>
            {/* <i className='mr-2 fa fa-lg fa-user' /> */}
            {piIcon}
            <div className='ml-2'>{headerText}</div>
          </div>
          <div className='fx fx__v-center'>
            {!isSaved
              ? <Button
                btnType='primary'
                onClick={handleOnSaveInsuranceData}
                style={{
                  width: '90px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}
              >
                Save Changes
              </Button>
              : <div style={{ color: blue[500] }}>
                <i className='fa fa-check mr-2' />
                Saved
              </div>
            }
            {!isSaved
              ? <i
                className={`ml-3 fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={onShowWarning}
              />
              : <div
                className='fx fx__hv-center ml-3'
                style={{
                  width: '24px',
                  height: '24px',
                  background: blue[200],
                  color: 'black',
                  fontSize: '20px'
                }}
                onClick={onClose}
              >
                <i
                  className='fa fa-compress'
                  onClick={onClose}
                />
              </div>
            }
          </div>
        </div>
        {/* Content */}
        <div className={css(styles.sideDrawerContentArea)}>
          {/* Payer Search */}
          <div
            ref={payerSearchRef}
            style={{
              position: 'relative',
              display: Object.keys(payer).length === 0 ? '' : 'none'
            }}
          >
            <div className='fx'>
              <TextInputWithLabel
                value={payerSearch}
                autoFocus
                showClearFieldIcon
                type='email'
                titleText="Search Payers"
                isCompactLayout={false}
                titleColor="#5079ab"
                width={320}
                showPreIcon
                preIcon="fa fa-search"
                iconSize={16}
                onChange={handleOnChangePayerSearch}
              />
              <Button
                btnType='primary'
                className='mt-3 ml-2'
                style={{ minWidth: '32px', minHeight: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={handleOnClickSearchPayers}
              >
                <i className='fa fa-search' />
              </Button>
            </div>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center'
              }}
            >
              {showPayerCards &&
                <PayerSearchCard
                  isNewPayer
                  payerName={payerSearch}
                  payerCode='N/A'
                  onAddNewPayer={() => {
                    setShowPayerCards(false);
                    setPayerSearchResults([]);
                    props.handleOnAddNewPayer();
                  }}
                />
              }

              {payerSearchResults.map((el) => {
                return (
                  <PayerCardLarge
                    className='mt-1'
                    height='300px'
                    hideEllipsis
                    key={el._id}
                    data={el}
                    showAddBtn
                    onAddPayer={() => {
                      setShowPayerCards(false);
                      setPayer(el);
                      setPayerSearch('');
                      setIsSaved(false);
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Payer Card */}
          <PayerCard
            selectedPayer={payer}
            onDelete={handleOnDeletePayer}
          />

          {/* Member relation to patient, firstName, middleName, lastName, dob etc */}
          <div className='mt-4'>
            {Object.keys(payer).length > 0 &&
              <div className='fx mt-4'>
                <div
                  className='mr-2 fx'
                  ref={memberRelationRef}
                  style={{ position: 'relative' }}
                >
                  Patient Relation To Subscriber:&nbsp;
                  <div
                    style={{
                      color: patientRelationToMember === '' ? salmon[500] : blue[500],
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowRelationMenu(true)}
                  >
                    {patientRelationToMember === '' ? 'Required' : patientRelationToMember}&nbsp;
                    <i className='fa fa-caret-down' />
                  </div>
                  <PopupMenu
                    top={22}
                    right={1}
                    visible={showRelationMenu}
                    parent={memberRelationRef}
                    position="absolute"
                    onOutsideClick={() => { setShowRelationMenu(false); }}
                  >
                    <div
                      className={css(styles.popupMenuContainer)}
                      style={{ width: '150px', height: '224px', maxHeight: '224px' }}
                    >
                      {/* Self */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Self' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Self' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Self');
                          setMemberFirstName(patientData.firstName);
                          setMemberMiddleName(patientData.middleName);
                          setMemberLastName(patientData.lastName);
                          const tmp1 = patientData.dob.split('T');
                          const tmp2 = tmp1[0].split('-');
                          const _year = +tmp2[0];
                          const _month = +tmp2[1];
                          const _date = +tmp2[2];
                          console.log(`_year = ${_year}, _month = ${_month} and _date = ${_date}`);
                          setMemberDobYear(_year);
                          setMemberDobMonth(_month - 1);
                          setMemberDobDate(_date);
                          setIsMemberDobPristine(false);
                          setShowMemberDobAlert(false);
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Self</div>
                      </div>
                      {/* Spouse */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Spouse' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Spouse' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Spouse');
                          setMemberFirstName('');
                          setMemberMiddleName('');
                          setMemberLastName('');
                          setIsMemberDobPristine(true);
                          setShowMemberDobAlert(true);
                          setMemberDobDate(TODAY.getDate());
                          setMemberDobMonth(TODAY.getMonth());
                          setMemberDobYear(TODAY.getFullYear());
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Spouse</div>
                      </div>
                      {/* Parent */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Parent' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Parent' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Parent');
                          setMemberFirstName('');
                          setMemberMiddleName('');
                          setMemberLastName('');
                          setIsMemberDobPristine(true);
                          setShowMemberDobAlert(true);
                          setMemberDobDate(TODAY.getDate());
                          setMemberDobMonth(TODAY.getMonth());
                          setMemberDobYear(TODAY.getFullYear());
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Parent</div>
                      </div>
                      {/* Legal Guardian */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Legal Guardian' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Legal Guardian' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Legal Guardian');
                          setMemberFirstName('');
                          setMemberMiddleName('');
                          setMemberLastName('');
                          setIsMemberDobPristine(true);
                          setShowMemberDobAlert(true);
                          setMemberDobDate(TODAY.getDate());
                          setMemberDobMonth(TODAY.getMonth());
                          setMemberDobYear(TODAY.getFullYear());
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Legal Guardian</div>
                      </div>
                      {/* Child */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Child' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Child' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Child');
                          setMemberFirstName('');
                          setMemberMiddleName('');
                          setMemberLastName('');
                          setIsMemberDobPristine(true);
                          setShowMemberDobAlert(true);
                          setMemberDobDate(TODAY.getDate());
                          setMemberDobMonth(TODAY.getMonth());
                          setMemberDobYear(TODAY.getFullYear());
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Child</div>
                      </div>
                      {/* Sibling */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Sibling' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Sibling' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Sibling');
                          setMemberFirstName('');
                          setMemberMiddleName('');
                          setMemberLastName('');
                          setIsMemberDobPristine(true);
                          setShowMemberDobAlert(true);
                          setMemberDobDate(TODAY.getDate());
                          setMemberDobMonth(TODAY.getMonth());
                          setMemberDobYear(TODAY.getFullYear());
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Sibling</div>
                      </div>
                      {/* Other */}
                      <div
                        className={css(styles.popupMenuItem)}
                        style={{
                          height: '32px',
                          padding: '8px',
                          borderLeft: patientRelationToMember === 'Other' ? `2px solid ${blue[500]}` : '',
                          background: patientRelationToMember === 'Other' ? blue[800] : ''
                        }}
                        onClick={() => {
                          setShowRelationMenu(false);
                          setPatientRelationToMember('Other');
                          setMemberFirstName('');
                          setMemberMiddleName('');
                          setMemberLastName('');
                          setIsMemberDobPristine(true);
                          setShowMemberDobAlert(true);
                          setMemberDobDate(TODAY.getDate());
                          setMemberDobMonth(TODAY.getMonth());
                          setMemberDobYear(TODAY.getFullYear());
                          setIsSaved(false);
                        }}
                      >
                        <div style={{ fontSize: '12px' }}>Other</div>
                      </div>
                    </div>
                  </PopupMenu>
                </div>
              </div>
            }
            {(patientRelationToMember && patientRelationToMember.length > 0) &&
              <>
                <div className='fx mt-2'>
                  <div>
                    <TextInputWithLabel
                      value={memberFirstName || ''}
                      titleText="First Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={109}
                      placeholder='Required'
                      onChange={handleOnEditMemberFirstName}
                      isDisabled={patientRelationToMember === 'Self'}
                    />
                  </div>
                  <div className='ml-2'>
                    <TextInputWithLabel
                      value={memberMiddleName || ''}
                      titleText="Middle Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={109}
                      onChange={handleOnEditMemberMiddleName}
                      isDisabled={patientRelationToMember === 'Self'}
                    />
                  </div>
                  <div className='ml-2'>
                    <TextInputWithLabel
                      value={memberLastName || ''}
                      titleText="Last Name"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={109}
                      placeholder='Required'
                      onChange={handleOnEditMemberLastName}
                      isDisabled={patientRelationToMember === 'Self'}
                    />
                  </div>
                </div>
                <div className='fx mt-4'>
                  <div className='mr-2'>
                    <i className='fa fa-calendar mr-2' />
                    Subscriber DOB
                  </div>
                  {/* eslint-disable */}
                  <DatePicker
                    left={-120}
                    right={0}
                    date={memberDobDate || TODAY.getDate()}
                    month={memberDobMonth || TODAY.getMonth()}
                    year={memberDobYear || TODAY.getFullYear()}
                    isPrisitine={isMemberDobPristine}
                    color={showMemberDobAlert ? salmon[500] : blue[400]}
                    noUnderline
                    isDisabled={patientRelationToMember === 'Self'}
                    onChangeDate={(year, month, date) => {
                      setMemberDobYear(year);
                      setMemberDobMonth(month);
                      setMemberDobDate(date);
                      setIsMemberDobPristine(false);
                      setShowMemberDobAlert(false);
                      setIsSaved(false);
                    }}
                  />
                  {/* eslint-enable */}
                </div>
                {/* Policy Number & Authorization Number */}
                <div className='fx mt-2'>
                  <div>
                    <TextInputWithLabel
                      value={policyNumber || ''}
                      titleText="Policy #"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={167}
                      placeholder='Required'
                      onChange={handleOnEditPolicyNumber}
                    />
                  </div>
                  <div className='ml-2'>
                    <TextInputWithLabel
                      value={authorizationNumber || ''}
                      titleText="Authorization #"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={167}
                      placeholder='Required'
                      onChange={handleOnEditAuthorizationNumber}
                    />
                  </div>
                </div>
                {/* Group Number */}
                <div className='fx mt-2'>
                  <div>
                    <TextInputWithLabel
                      value={groupNumber || ''}
                      titleText="Group #"
                      titleFontSize={12}
                      isCompactLayout={true}
                      titleColor={gray[200]}
                      textColor="white"
                      selectionColor="white"
                      selectionBackground="#0080FF"
                      spellcheck={false}
                      width={167}
                      placeholder='Required'
                      onChange={handleOnEditGroupNumber}
                    />
                  </div>
                </div>
              </>
            }
          </div>

          {(patientRelationToMember && patientRelationToMember.length > 0) &&
            <>
              {/* Primary Insurance Card - Front */}
              <div className='mt-4'>
                <div className='fx'>
                  {piIcon}
                  <div className='ml-2'>
                    Insurance Card (Front)
                  </div>
                </div>
                {showFrontLoader
                  ? <Loader iconType="cog" iconSize="3x" text="Uploading Insurance ID(Front)" />
                  : <div style={{ position: 'relative' }}>
                    <img
                      className='mt-2'
                      ref={frontCardRef}
                      src={insuranceIdFrontUrl}
                      style={{
                        width: '343px',
                        maxWidth: '343px',
                        height: 'auto'
                      }}
                    />
                    <div
                      onClick={onInsuranceCardFrontCameraClick}
                      className={css(styles.drawerBannerCameraIcon)}
                    >
                      <i className="fa fa-camera" />
                    </div>
                  </div>
                }
              </div>
              {/* Primary Insurance Card - Back */}
              <div className='mt-4'>
                <div className='fx'>
                  {piIcon}
                  <div className='ml-2'>
                    Insurance Card (Back)
                  </div>
                </div>
                {showBackLoader
                  ? <Loader iconType="cog" iconSize="3x" text="Uploading Insurance ID(Back)" />
                  : <div style={{ position: 'relative' }}>
                    <img
                      className='mt-2'
                      ref={backCardRef}
                      src={insuranceIdBackUrl}
                      style={{
                        width: '343px',
                        maxWidth: '343px',
                        height: 'auto'
                      }}
                    />
                    <div
                      onClick={onInsuranceCardBackCameraClick}
                      className={css(styles.drawerBannerCameraIcon)}
                    >
                      <i className="fa fa-camera" />
                    </div>
                  </div>
                }
              </div>
            </>
          }
        </div>
      </div>
    </Drawer>
  );
};

export default InsuranceDrawer;
